// The six scenarios. Each names its pack, the sims it can run on (candidates in order of
// preference), the presets the left panel offers, the timeline it plays until a sim supplies its
// own, and the one-line Methods text. A loaded sim's header replaces the timeline and the Methods,
// so the panel always names the model and data actually on screen.

import type { Mode, Orbit, Preset as CameraPreset, SectionPlane, Target } from '../viewport/api.ts';
import { tokens } from '../../../../design/system/tokens.ts';
import type { RampName } from '../ui/scales.ts';

export type ScenarioId = 'explore' | 'bloodflow' | 'stroke' | 'seizure' | 'neurons' | 'drugs' | 'mind' | 'stim' | 'lifetime';

export interface ScenePreset {
  id: string;
  name: string;
  /** One short line under the name. */
  hint: string;
  camera: CameraPreset;
  /** An exact camera instead of `camera`'s framing, for a site no structure frames (where a specimen came from). */
  orbit?: Orbit;
  /** Frames the cells the scene's run draws (a cell's arbor, a circuit) from this side, fitted to the
   *  canvas the panels leave clear, once the run is in; until then, and where none is drawn, `orbit` or
   *  `camera` as above. */
  frameCells?: { yawDeg: number; pitchDeg: number };
  /** Layer modes by system; systems not named keep the pack's default. */
  layers?: Record<string, Mode>;
  isolate?: Target;
  section?: SectionPlane[];
  /** Structures the view hides, by id (a hemisphere, for a medial view); the hand-made overrides start from these. */
  hide?: string[];
  select?: string;
  /** The electrode sites the view draws over the picture (spec §15, Electrodes); a kind not named keeps its switch. */
  electrodes?: Partial<Record<'eeg' | 'seeg', boolean>>;
  /** Structures the view names first, as design's screens pick each state's callouts; one out of sight
   *  is named at its centroid, its pill saying hidden and its leader dashed (Seizure's onset zone). */
  callouts?: string[];
  time?: number;
  /** The playback rate the view sets (scene seconds a second), one its timeline offers: Stroke's depolarizations at 1 min/s. */
  speed?: number;
  /** A connectome or tissue pack (connectome-pack-v0) the view loads instead of the scene's, such as H01's
   *  cortex. The scene's sims aren't drawn there, and a viewport without such packs isn't offered it. With
   *  `packKind: 'anatomy'`, an anatomy pack (Lifetime's Parkinson's stages on the MNI template), which every viewport loads. */
  pack?: string;
  packKind?: 'anatomy' | 'connectome';
  /** The panel's subtitle while the view is on, where the scene's own doesn't fit it (Lifetime's Parkinson's stages). */
  tagline?: string;
  /** A glow the view lays over the tissue in front of a structure (viewport `glow`, 0.3): Seizure's deep
   *  onset zone, whose light reaches the lateral surface. Drawn while the scene's own model plays with its
   *  activity on. */
  glow?: { at: string; radiusMm: number; color: string; opacity: number };
  /** A still comparison the view draws itself through the live vertex layer (Drugs' measured ΔFC, features/Dfc.tsx):
   *  the scene's clock and runs stand aside while it's on (app/scene.ts timelineOf, viewport/bridge.ts simCommands). */
  still?: boolean;
  /** A journey the viewport plays over the scene's pack (viewport `journey`, 0.3 additive, engines/wgpu #778): Explore's Powers
   *  of Ten. `duration` (s) gives the transport its length before the viewport's reply brings the stages (features/journey.ts);
   *  the scene's clock is the journey's, and a viewport that doesn't list it (`ready.journeys`) isn't offered the view. */
  journey?: { id: string; duration: number };
  /** The scene's runs stand aside while the view draws its own run through the viewport (Blood flow's 3D flow,
   *  features/cfd.ts): the bridge sends none of them and Methods lists the view's, as for `still`; unlike `still`,
   *  the clock plays, and the view's run follows it. */
  aside?: boolean;
  /** Systems the view hides by id, every member, as an isolation's members show whatever their system's layer says
   *  (viewport-api-v0, visibility): 3D flow hides the arteries its lumen stands in for. */
  hideSystems?: string[];
  /** A viewport layer the view draws through (`ready.layers`, `name:` covering a family); a viewport that doesn't list
   *  it isn't offered the view: 3D flow's `surface:wall`. */
  needsLayer?: string;
}

export interface TimelineEvent {
  t: number;
  label: string;
  /** Label priority where labels would collide: lower ranks keep theirs (default 0). An empty label is a tick alone. */
  rank?: number;
}

export interface Timeline {
  /** Seconds of scenario time: where the timeline ends. */
  duration: number;
  /** Where it starts, s, where the run's first frame is after 0 (a circuit recorded after its settling time); else 0. */
  start?: number;
  /** How the readout shows time. `week` reads the clock in weeks from `origin` (the fetal atlas's gestational age). */
  unit: 'ms' | 's' | 'min' | 'h' | 'week';
  /** What t = 0 reads as, in the unit (21 for a sequence whose first frame is 21 gestational weeks). */
  origin?: number;
  /** Loops at the end (a cardiac cycle). */
  periodic?: boolean;
  events?: TimelineEvent[];
  /** Playback rates offered, in scenario seconds per wall second. */
  speeds: number[];
  /** A clip's own slow motion (cells-sim-v0 `time.pacing`), offered as the Paced speed: from each `t`, `rate`. */
  pacing?: { t: number; rate: number }[];
}

/** One sim slot of a scene: the viewport's `sim` slot, filled by the first candidate that exists. */
export interface SimSlotSpec {
  slot: string;
  /** Run directories under sims/, most preferred first. `{id}` takes the value of the scene's choice `id`. */
  candidates: string[];
  /** Methods heading for a secondary slot ('Flow', 'Before the clot'). */
  label?: string;
  /** Loaded for traces and Methods only; the viewport isn't asked to draw it. */
  appOnly?: boolean;
  /** Template values its runs were made for: the slot is empty unless the scene's match (Stroke's
   *  spreading depolarization, run for one clot). */
  when?: Record<string, string>;
  /** Drawn in the viewport in that slot's place, when it finds a run: that slot's run then serves the
   *  traces and Methods alone (Neurons' close-up of a circuit, placed and with every compartment of its
   *  cells, while the raster reads the whole circuit). */
  drawnFor?: string;
  /** The ramp its run's layer draws on, design's call rather than the run's kind: Mind's sweep takes `drive`,
   *  a stimulus and not firing. Without one, a dynamics run takes the activity ember (`sd` for a spreading
   *  depolarization), and a cells run's arbors the ember too. */
  ramp?: RampName;
}

export interface ChoiceOption {
  value: string;
  label: string;
  hint?: string;
  /** Template values this option fixes over the Stroke slice's: v5's one collateral grade, whatever
   *  the switch says (its good, intermediate and poor runs are identical, ba-blood #536). */
  sets?: Record<string, string>;
  /** Neurons' Model and Cells sections: one cell's run, or a microcircuit's (`circuit`) in a `condition`. */
  circuit?: string;
  condition?: string;
  /** The scene's tagline in its panel while this option plays, where the scene's own differs. */
  tagline?: string;
  /** The head this option's runs were made on (Stimulate's subjects): the scene loads it in place of its own pack while
   *  the option is chosen (`packOf`, app/scene.ts), and the bridge reloads on the change. */
  pack?: string;
  /** Its run draws in its own frame, with no brain (the engine's frame scene, the column's close-up): in a view that frames its
   *  cells, the scene loads the frame the run's header names in place of its pack (`packOf`, app/scene.ts). Elsewhere the scene's
   *  pack stays, and the run, placed nowhere in it, isn't drawn. */
  frame?: boolean;
}

/** A scene parameter that picks between runs: a drug, a collateral grade, a treatment time. */
export interface SceneChoice {
  id: string;
  label: string;
  options?: ChoiceOption[];
  /** Options listed from the first of these directories with an index.json; a value is the run's path. With `options`, those name
   *  runs by their index id, in that order, under their own labels. */
  index?: string[];
  /** Index rows of these kinds only. */
  kinds?: string[];
  /**
   * Index runs that stand in for older ones, by id: where both are listed, the run takes the place
   * and name of the first it replaces, and the others leave the list.
   */
  supersede?: Record<string, string[]>;
  /** An option value (or, for an index choice, a run id). */
  default?: string;
  /** Its value comes from the Stroke slice (app/stroke.ts), not a control of its own; the options still
   *  say which runs exist. */
  fromSlice?: boolean;
  /** The scene's own sections draw its controls (Neurons' Model and Cells), not the Scenario rows. */
  own?: boolean;
}

export interface Scenario {
  id: ScenarioId;
  name: string;
  /** Keyboard shortcut digit. */
  key: string;
  tagline: string;
  pack: string;
  sims?: SimSlotSpec[];
  choices?: SceneChoice[];
  /** Runs a view of the scene's own reads by path, outside its sim slots (Drugs' measured ΔFC): never one of its
   *  choices' options, and never loaded with its runs. */
  claims?: string[];
  /** The vessel a flow trace shows until one is selected. */
  traceDefault?: string;
  /**
   * A solver fast enough to re-run while you drag (hemo-rt: cut a vessel anywhere along it, flows
   * recompute), under sims/; the clot the scene opens with; `clinical` for Stroke's collaterals,
   * thrombectomy and tissue-run controls (app/stroke.ts); and hemo's ISLES'24 comparison exports, most
   * preferred first, for Compare with ISLES'24.
   */
  live?: { solver: 'hemo-rt'; path: string; clot?: { vessel: string; s: number }; clinical?: boolean; isles?: string[]; islesModel?: string };
  /** The one-line Methods caption under the viewport: model · data. */
  caption: string;
  /** Methods text until a sim header supplies its own. */
  methods: string;
  /** What the producing lane asked Methods to keep about a run its header doesn't say, by run id; it follows the header's text. */
  notes?: Record<string, string>;
  presets: ScenePreset[];
  timeline?: Timeline;
  /** `live`: needs no sim (Explore); `preview` until one of its sims loads and is real. */
  status: 'live' | 'preview';
  /** Its left panel must fit without scrolling (spec §1, Explore): where it wouldn't, Views collapses to its active row (§5). */
  fit?: boolean;
  /** Systems the scene leaves out: not in its layer list, never drawn, never labelled. */
  omit?: string[];
  /** The only systems the scene lists, as design's screens list them; the others are left out as `omit` leaves them. */
  systems?: string[];
  /** The moment the scene opens at, in its own time (seconds): design's screens' default state. */
  start?: number;
}

/** Whether a scene leaves a system out: not in its layer list, never drawn, never labelled. */
export const leavesOut = (sc: Pick<Scenario, 'omit' | 'systems'>, system: string) => !!sc.omit?.includes(system) || (!!sc.systems && !sc.systems.includes(system));

/**
 * hemo's network and tissue model, for Blood flow and Stroke. v5 is current (#536): its network has
 * explicit pial links, and its tissue runs come out the same for every collateral grade (no spatial
 * collateral model yet), so the scene takes the intermediate one and the panel says the grade is
 * inactive. v4 stays selectable as the previous model. Runs are never rewritten (#270); v2 and v3
 * carry openBF's taper bug (#411), so they aren't offered.
 */
const HEMO_MODEL: SceneChoice = {
  id: 'model',
  label: 'Model',
  default: 'v5',
  options: [
    { value: 'v5', label: 'v5', sets: { grade: 'intermediate' } },
    { value: 'v4', label: 'Previous', hint: 'v4, the previous model' },
  ],
};

/** IXI 165, the person behind Blood flow and Stroke: hemo's network is that subject's own vessels (decision 0006 §4). The scenes
 *  load this person's own cortex (FastSurfer's surfaces, with maps from fsaverage) composed over ixi-165-v0: the engine draws it in
 *  place of the parcel meshes and plays fsaverage runs on their folds (engines/wgpu #541). */
const SUBJECT = 'ixi-165-cortex-hd';
/** The spreading depolarization for hemo's proximal left M1 clot, driven by each model's tissue state at occlusion: fsaverage,
 *  0 to 30 min after it. ba-blood ran v5's (#582, header fe787b4d), dynamics v4's. */
const SD_V5 = 'spreading-depolarization-ixi165-m1-v5-841dcfd0-t0-v1';
/** ba-blood's 3D lattice Boltzmann CFD through IXI 165's own lumen (hemo-sim-v0 kind 3d-cfd; engines/wgpu #815), the run
 *  Blood flow's 3D flow view draws (features/cfd.ts). The full run: 100,000 tracers and f32 fields, 515 MB; kept for a
 *  one-line switch back. */
export const CFD_FULL_RUN = 'hemo/ixi165-cfd-v2-baseline';
/** ba-engine's display copy of it (#830; ba-blood confirmed the id on #2 at 10:20 UTC 10-03, public since 10:51 UTC): the
 *  20,000 drawn tracers, their speeds and the wall shear quantised to u16, the wall and the static fields unchanged, 83 MB in
 *  all; format ba-hemo-sim-v0.1, read by embeds from d88ca518 (the one ba-app pulls next). */
export const CFD_DISPLAY_RUN = 'hemo/ixi165-cfd-v2-baseline-display-v1';
/** The one place the view's run id lives. */
export const CFD_RUN: string = CFD_DISPLAY_RUN;

/** Lifetime's measured track: ba-life's dHCP fetal white-matter surface atlas, 21–36 gestational weeks (lifetime-sim-v0, sixteen
 *  weekly frames, both hemispheres, curvature and sulcal-depth maps), on the pack of its week-21 geometry. Its time base is
 *  seconds since 21 weeks, a frame a week, so the scene's clock reads in weeks. The model track (LIFE-014's Newton fold,
 *  ba-life's export, #2) joins as a second slot when it's published. */
export const LIFETIME_ATLAS = 'lifetime/dhcp-fetal-wm-21to36-v1';
/** Lifetime's second measured sequence: ba-life's dHCP neonatal white-matter atlas at 37, 40 and 44 weeks postmenstrual age
 *  (lifetime-sim-v0, three frames, both hemispheres, no maps), assembled for display in one bilateral frame (a mirror-symmetry pose
 *  with the hemispheres 2 mm apart by convention, not measured anatomy or author coordinates), on pack dhcp-neonatal-wm-v3 0.3.0 in
 *  dHCPNeonatalSym40Display. Its clock is its own, seconds since 37 weeks PMA. Nothing joins it to the fetal sequence (another cohort
 *  in another space): its header claims no fetal-to-neonatal correspondence. */
export const LIFETIME_NEONATAL = 'lifetime/dhcp-neonatal-wm-37to44-v3';
/** Lifetime's model track: ba-life's LIFE-014 Newton fold as closed 2D sections beside the atlas's own (lifetime-sections-v0, app-read;
 *  #862, live on the public app 10-03). The app draws the overlay; the engine keeps morphing the atlas. */
export const LIFETIME_MODEL = 'lifetime/life014-newton-sections-v1';
/** LIFE-020's assigned-thickness sections (lifetime-thickness-sections-v0, ba-life's draft contract at 21168c51): four fine-mesh
 *  tracks on an imposed-growth axis, app-read for Lifetime's Assigned thickness track; published after ba-life's validation. */
export const LIFETIME_ASSIGNED = 'lifetime/life020-thickness-sections-v1';
/** ba-life's ENIGMA-PD stage maps (lifetime-regions-v0, LIFE-019): measured cortical thinning by Hoehn and Yahr stage beside a
 *  frozen connectome model's samples, on the MNI template's DKT structures; the scene's Parkinson's stages view (features/lifetime.ts). */
export const LIFETIME_PD = 'lifetime/enigma-pd-stages-v1';
/** ba-life's BrainChart export (lifetime-centiles-v0, chart-only, read by id): GMV, WMV and ventricular-volume centiles by sex over
 *  post-conception age; the strip's Centiles tab (features/Lifetime.tsx). Never sent to the viewport. */
export const LIFETIME_CENTILES = 'lifetime/brainchart-centiles-v1';
/** The Parkinson's stages view's id (a still view on the MNI template; features/lifetime.ts pdView). */
export const PD_VIEW = 'pd';
/** The Cortical thickness view's id (a still view on fsaverage's Destrieux cortex; features/lifetime.ts thicknessView). */
export const THICKNESS_VIEW = 'thickness';
/** ba-life's Rutherford 2022 normative thickness reference (lifetime-thickness-v0, sampled by the app into a live vertex layer; never a
 *  sim slot): predictive median cortical thickness per Destrieux parcel, 2–100 years since birth, by sex, on pack fsaverage-destrieux-v1
 *  0.1.0 (docs/contracts/lifetime-thickness-v0.md). */
export const LIFETIME_THICKNESS = 'lifetime/rutherford-thickness-v1';
export const WEEK_S = 604800;
/** Weeks a second: a quarter, a half, one and two, so the fifteen weeks play in 60, 30, 15 or 7.5 s. */
export const LIFETIME_SPEEDS = [WEEK_S / 4, WEEK_S / 2, WEEK_S, 2 * WEEK_S];
const LIFETIME_LAYERS: Record<string, Mode> = { 'white-matter': 'shown' };
/** Lifetime's Period: which measured sequence the scene plays. Each option brings the pack of its own geometry (ChoiceOption.pack:
 *  the bridge reloads on the change) and fixes `{sequence}` for the main slot (`sets`, as Stimulate's subjects do); `period=neonatal`
 *  in a link. The model track (LIFE-014, grown from the 21-week section) belongs to the fetal period alone, by its slot's `when`. */
export const LIFETIME_PERIOD: SceneChoice = {
  id: 'period',
  label: 'Period',
  options: [
    { value: 'fetal', label: 'Fetal', hint: 'The dHCP fetal atlas, 21–36 gestational weeks (Karolis et al. 2023)', pack: 'dhcp-fetal-wm-v1', sets: { sequence: 'dhcp-fetal-wm-21to36-v1' } },
    {
      value: 'neonatal',
      label: 'Around birth',
      hint: 'The dHCP neonatal atlas, 37, 40 and 44 weeks postmenstrual age, both hemispheres in one display frame',
      pack: 'dhcp-neonatal-wm-v3',
      sets: { sequence: 'dhcp-neonatal-wm-37to44-v3' },
      tagline: 'A cortex around birth: 37 to 44 weeks postmenstrual age',
    },
  ],
  default: 'fetal',
};
/** 3D flow's camera: the film's HERO_BELOW (engines/wgpu flow.rs: yaw 0, pitch −70°, framed on the circle) aimed at the centre of
 *  the lumen's 31 cut faces (the run's caps.json: x −34.4…32.3, y 3.9…63.3, z −8.7…21.7 mm in IXI 165's space), 170 mm out so the
 *  circle fills the view's middle with the ghosted brain at the edges. Recompute the target if the run changes. */
export const CFD_ORBIT: Orbit = { targetMm: [-1.1, 33.6, 6.5], yawDeg: 0, pitchDeg: -70, distanceMm: 170 };
const SD_V4 = 'spreading-depolarization-ixi165-m1-v4-8563700d-t0-v1';
/** The MNI152 2009c template, for Explore and for runs on template anatomy (Seizure's are fsaverage): the engine draws BigBrain's HD
 *  cortex over v0's cortex meshes (decision 0006; the template's own HD cortex where a node lacks BigBrain's), and plays surface runs
 *  on it through its maps. */
const TEMPLATE = 'mni152-2009c-v0';
/** Activity is on the brain: the template's vessels, sinuses and nerves would only cover it. */
const BRAIN_ONLY: Record<string, Mode> = { artery: 'hidden', vein: 'hidden', sinus: 'hidden', 'cranial-nerve': 'hidden' };
const SEIZURE_LAYERS: Record<string, Mode> = { subcortical: 'hidden', artery: 'shown' };
/** The onset glow's colour: the activity scale's 0.75 stop (`--ba-scale-activity-3`), as design's Seizure screen paints it. */
const ONSET_GLOW = tokens.scale.activity.$value.find((x) => x.position === 0.75)?.color ?? '#FFB347';

export const SCENARIOS: Scenario[] = [
  {
    id: 'explore',
    name: 'Explore',
    key: '1',
    tagline: 'A reference brain: BigBrain’s cortex, the template’s structures',
    // An MNI scene (decision 0006 §4, clarified 2026-10-02): BigBrain's cortex over v0's 836 structures.
    pack: TEMPLATE,
    caption: 'BigBrain cortex (Amunts 2013) in MNI 2009c · DKT parcels (Lewis 2020) · template nuclei · atlas arteries',
    methods:
      'Cortex: BigBrain, one person’s brain rebuilt from 7,404 histological sections at 20 µm (Amunts et al. 2013), placed in MNI152 2009c by an affine, with DKT parcels per vertex (Lewis et al. 2020). Around it, the MNI152 2009c template’s: FastSurfer’s deep nuclei, cerebellum and brainstem, Julich-Brain v3.1 areas, HCP-1065 tractography (Yeh 2022), arteries from the Mouches and Forkert atlas (2019) and Z-Anatomy’s cranial nerves.',
    status: 'live',
    fit: true,
    // Design's Explore lists nine systems (screens README, #548): v0's veins and dural sinuses would only cover the cortex.
    omit: ['vein', 'sinus'],
    presets: [
      { id: 'whole', name: 'Whole brain', hint: 'Cortex, deep nuclei and cerebellum', camera: 'hero' },
      { id: 'deep', name: 'Deep nuclei', hint: 'Basal ganglia, thalamus, hippocampus', camera: 'hero', isolate: { system: 'subcortical' } },
      { id: 'tracts', name: 'White-matter tracts', hint: 'Tractography bundles', camera: 'left', isolate: { system: 'tract' } },
      { id: 'arteries', name: 'Arteries', hint: 'Circle of Willis and branches', camera: 'inferior', isolate: { system: 'artery' } },
      { id: 'nerves', name: 'Cranial nerves', hint: 'From the brainstem outwards', camera: 'inferior', isolate: { system: 'cranial-nerve' } },
      // As design's explore-section-axial plate: the hero camera over the far side of z = +10 mm.
      { id: 'section', name: 'Axial section', hint: 'MRI slice through the deep nuclei', camera: 'hero', section: [{ axis: 'axial', mm: 10, keep: 'negative' }] },
      // Powers of Ten (ba-web's docs/areas/web-powers-journey.md; engines/wgpu #778, #928): the engine's native 30 s dive from the
      // whole brain through one of H01's neurons and its synapse to one NMDA receptor, drawn over this scene and driven by its clock;
      // in links, `journey=powers-of-ten`.
      { id: 'powers', name: 'Powers of Ten', hint: 'From a head to one receptor, in 30 s', camera: 'hero', journey: { id: 'powers-of-ten', duration: 30 }, time: 0, speed: 1 },
    ],
  },
  {
    id: 'bloodflow',
    name: 'Blood flow',
    key: '2',
    tagline: 'Pulsatile flow through the Circle of Willis',
    // Stroke's pack too, so switching between the two loads nothing (about 4 s each way on the Mac otherwise).
    pack: SUBJECT,
    choices: [HEMO_MODEL],
    sims: [{ slot: 'main', candidates: ['hemo/ixi165-1d-{model}-baseline', 'hemo/ixi165-synth1d-baseline'] }],
    traceDefault: 'ba:left-mca-m1',
    live: { solver: 'hemo-rt', path: 'hemo/hemo-rt-axial-v0.2' },
    caption: '1D pulsatile flow · Circle of Willis from IXI 165',
    methods: '1D pulsatile blood flow on the cerebral arteries (hemo lane). The sim is on its way; anatomy is this person’s.',
    status: 'preview',
    // Design's Blood flow screens: the cortex and cerebellum around the arteries, at 300 ms of the cycle.
    systems: ['cortex', 'cerebellum', 'artery'],
    start: 0.3,
    timeline: { duration: 1.1, unit: 'ms', periodic: true, speeds: [0.1, 0.25, 0.5, 1], events: [{ t: 0.12, label: 'Systolic peak' }, { t: 0.42, label: 'Dicrotic notch' }] },
    presets: [
      { id: 'cow', name: 'Circle of Willis', hint: 'From below, the brain ghosted', camera: 'inferior', isolate: { system: 'artery' } },
      // The CFD inside its own lumen, framed as the film frames the circle (the engine's HERO_BELOW, flow.rs: yaw 0, pitch −70°,
      // from below with a 20° anterior tilt) on the lumen's own centre, the brain ghosted around it as context: the pack's
      // arteries hidden by id (the lumen stands in for them), the 1D runs standing aside; only a viewport with surface layers
      // offers it (#815). The isolation's inferior framing put the circle in about 15 % of the view (the lead, 10-03).
      { id: 'cfd', name: '3D flow', hint: 'Lattice Boltzmann flow inside this person’s own lumen', camera: 'inferior', orbit: CFD_ORBIT, isolate: { system: 'artery' }, hideSystems: ['artery'], aside: true, needsLayer: 'surface:wall' },
      { id: 'mca', name: 'Middle cerebral arteries', hint: 'Both MCAs from the front', camera: 'anterior', isolate: { system: 'artery' } },
      { id: 'context', name: 'In the brain', hint: 'Arteries inside the cortex', camera: 'hero', layers: { cortex: 'ghost', artery: 'shown' } },
    ],
  },
  {
    id: 'stroke',
    name: 'Stroke',
    key: '3',
    tagline: 'A clot in the left M1, collaterals, penumbra and reperfusion',
    pack: SUBJECT,
    choices: [
      HEMO_MODEL,
      {
        id: 'grade',
        label: 'Collaterals',
        default: 'good',
        fromSlice: true,
        options: [
          { value: 'good', label: 'Good' },
          { value: 'intermediate', label: 'Fair', hint: 'Intermediate collateral grade' },
          { value: 'poor', label: 'Poor' },
        ],
      },
      {
        id: 'treatment',
        label: 'Thrombectomy',
        default: 'untreated',
        fromSlice: true,
        options: [
          { value: 'untreated', label: 'None', hint: 'Untreated' },
          { value: 'reperf-1h', label: '1 h', hint: 'Reperfusion at 1 hour' },
          { value: 'reperf-3h', label: '3 h', hint: 'Reperfusion at 3 hours' },
          { value: 'reperf-6h', label: '6 h', hint: 'Reperfusion at 6 hours' },
        ],
      },
    ],
    sims: [
      {
        slot: 'main',
        // {site} is the clot's site (app/stroke.ts): no run for a site hemo didn't simulate. Each
        // model's tissue runs were driven by its own flows beside them; the earlier (#242) runs by
        // v2's, which has openBF's taper bug, so they aren't fallbacks (#411).
        // {part} is '-distal' for an M1 clot beyond the lenticulostriate origin (app/stroke.ts).
        // {anatomy} and {pcom} are a basilar clot's PCom anatomy (app/stroke.ts anatomyOf): the tissue runs name both
        // anatomies, the flows only the traced one, and the anatomy and its flows change together. {occl} is the
        // flows' name for the site (`ba` for the basilar). All three are empty, or the site, for the other clots.
        candidates: ['hemo/ixi165-stroke-{model}-{site}-{grade}-{treatment}{part}{anatomy}', 'hemo/ixi165-1d-{model}-occl-{occl}{part}{pcom}', 'hemo/ixi165-synth1d-occl-{site}{part}'],
      },
      { slot: 'flow', label: 'Flow', candidates: ['hemo/ixi165-1d-{model}-occl-{occl}{part}{pcom}', 'hemo/ixi165-synth1d-occl-{site}{part}'] },
      // Complete reperfusion restores that anatomy's own baseline.
      { slot: 'reference', label: 'Before the clot', appOnly: true, candidates: ['hemo/ixi165-1d-{model}-baseline{pcom}', 'hemo/ixi165-synth1d-baseline'] },
      // On IXI 165's own cortex through its map from fsaverage, for the proximal clot at any grade and
      // arm, driven by the model on screen: v5's own run (v4's behind it where a node lacks v5's, its
      // penumbra overlapping v5's at Dice 0.94, notes), and the previous model's.
      { slot: 'sd', label: 'Spreading depolarization', candidates: [`dynamics/${SD_V5}`, `dynamics/${SD_V4}`], when: { model: 'v5', site: 'l-m1', part: '' } },
      { slot: 'sd', label: 'Spreading depolarization', candidates: [`dynamics/${SD_V4}`], when: { model: 'v4', site: 'l-m1', part: '' } },
    ],
    // Measured by ba-app on 10-02 from the runs' tissue-state at t = 0 (penumbra within the brain mask); a random mask of the
    // same size scores 0.21.
    notes: {
      [SD_V5]:
        'Driven by the v5 run on screen as it was at occlusion: hemo v5’s proximal left M1 tissue state (network 841dcfd0), where every v5 grade and arm starts. The model ran the first 30 minutes after occlusion, so the layer ends there.',
      [SD_V4]:
        'Driven by hemo v4’s proximal left M1 run with intermediate collaterals, as it was at occlusion. That run’s penumbra overlaps v5’s at Dice 0.94 (every v5 grade is the same), v4’s poor-collateral run’s at 0.94 and its good-collateral run’s at 0.83. The model ran the first 30 minutes after occlusion, so the layer ends there.',
    },
    traceDefault: 'ba:left-mca-m1',
    // The clot opens where hemo's proximal M1 clot is, 18 % along it (app/stroke.ts M1_CLOTS).
    live: { solver: 'hemo-rt', path: 'hemo/hemo-rt-axial-v0.2', clot: { vessel: 'ba:left-mca-m1', s: 0.18 }, clinical: true, isles: ['hemo/isles24-model-m1-ica-v5', 'hemo/isles24-model-m1-ica-v2'], islesModel: 'v4' },
    caption: 'Left M1 occlusion · 1D flow and tissue fate · IXI 165',
    methods: 'Left MCA M1 occlusion: 1D flow redistribution, perfusion deficit and tissue fate (hemo lane). The sim is on its way.',
    status: 'preview',
    // Design's Stroke screens list the cortex the deficit is on and the arteries the clot is in.
    systems: ['cortex', 'artery'],
    // Until its runs load: they run 24 h from the occlusion, and mark their own reperfusion.
    timeline: {
      duration: 24 * 3600,
      unit: 'h',
      speeds: [60, 600, 1800, 3600, 7200],
      events: [{ t: 0, label: 'Occlusion' }],
    },
    presets: [
      { id: 'occlusion', name: 'Occlusion', hint: 'Left M1, from the front', camera: 'anterior', isolate: { system: 'artery' } },
      { id: 'territory', name: 'MCA territory', hint: 'The cortex the left MCA feeds', camera: 'left', layers: { cortex: 'shown', artery: 'shown' } },
      { id: 'slice', name: 'Perfusion slice', hint: 'Axial slice through the deficit', camera: 'superior', section: [{ axis: 'axial', mm: 20, keep: 'negative' }] },
      // As ba-engine's #541 frame: the waves across the left hemisphere at 20 min.
      { id: 'sd', name: 'Depolarizations', hint: 'Waves across the penumbra at 20 min', camera: 'left', layers: { cortex: 'shown', artery: 'shown' }, time: 1200, speed: 60 },
    ],
  },
  {
    id: 'seizure',
    name: 'Seizure',
    key: '4',
    tagline: 'A left mesial temporal onset that recruits the network',
    pack: TEMPLATE,
    // The model alone (its scalp EEG and depth SEEG in volts, from distributed cortical sources), or a
    // real patient's recorded seizure beside the model fitted to it (the HUP fits).
    choices: [
      {
        id: 'run',
        label: 'Real patient',
        index: ['dynamics'],
        default: 'seizure-left-mesial-temporal-lateral-spread-distributed-v2',
        options: [
          { value: 'seizure-left-mesial-temporal-lateral-spread-distributed-v2', label: 'None', hint: 'The model alone: a left mesial temporal onset that spreads across the temporal lobe, with scalp EEG and depth SEEG' },
          { value: 'hup-seizure-sub-hup185', label: 'HUP185', hint: 'Their recorded seizure beside the model fitted to it: SEEG, 103 contacts' },
          { value: 'hup-seizure-sub-hup070', label: 'HUP070', hint: 'Their recorded seizure beside the model fitted to it: ECoG grid, 54 contacts' },
        ],
      },
    ],
    sims: [{ slot: 'main', candidates: ['{run}', 'dynamics/seizure-left-mesial-temporal-lateral-spread-distributed-v2', 'dynamics/seizure-left-mesial-temporal-lateral-spread-distributed-v1', 'dynamics/seizure-left-mesial-temporal-v1', 'dynamics/seizure-left-mesial-temporal'] }],
    caption: 'Epileptor network · structural connectome · MNI152 2009c',
    methods: 'Epileptor network model on a structural connectome (dynamics lane), on the MNI152 2009c template brain.',
    // Design's Seizure screens: the cortex, the deep onset zone and the arteries; the cerebellum is in
    // their picture (its arteries would hang in the air without it), though not in their Layers.
    systems: ['cortex', 'subcortical', 'cerebellum', 'artery'],
    // Dynamics' note on the sensor amplitudes (#2, 2026-10-01): the source density is set in advance, not fitted.
    notes: {
      'seizure-left-mesial-temporal-lateral-spread-distributed-v2': 'The scalp amplitude hasn’t been fitted to a reference recording.',
      'seizure-left-mesial-temporal-lateral-spread-distributed-v1': 'The scalp amplitude hasn’t been fitted to a reference recording.',
    },
    status: 'preview',
    timeline: { duration: 60, unit: 's', speeds: [0.25, 0.5, 1, 2, 4], events: [{ t: 10, label: 'Onset' }] },
    presets: [
      // Design's three moments of the model run (design/system/screens/seizure.html): the mesial onset
      // (3.1 s) just after it starts, and the front crossing the temporal lobe (12 parcels at 16.6 s,
      // its widest). Design's third, both hemispheres at 27 s, is extrapolated past the run: the run
      // stays in the left hemisphere and reaches the orbitofrontal cortex and cingulate by 22 s, so
      // Spread shows that, from below.
      // Each draws the cortex and its arteries with the deep nuclei hidden, as design's Layers do; the
      // onset zone is named through the cortex.
      // Design's onset glow: 15 mm around the hippocampus's centroid in the activity scale's 0.75 stop, at 85 %.
      {
        id: 'onset',
        name: 'Onset',
        hint: 'The left hippocampus, from the side',
        camera: 'left',
        layers: SEIZURE_LAYERS,
        callouts: ['ba:left-hippocampus'],
        electrodes: { eeg: false, seeg: false },
        time: 4,
        glow: { at: 'ba:left-hippocampus', radiusMm: 15, color: ONSET_GLOW, opacity: 0.85 },
      },
      // With its 10–20 sites on: T7 lights over the temporal lobe as the front crosses it.
      { id: 'recruitment', name: 'Recruitment', hint: 'The front across the temporal lobe', camera: 'left', layers: SEIZURE_LAYERS, electrodes: { eeg: true }, time: 16.6 },
      {
        id: 'spread',
        name: 'Spread',
        hint: 'Into the orbitofrontal cortex, from below',
        camera: 'inferior',
        layers: SEIZURE_LAYERS,
        callouts: ['ba:left-lateral-orbitofrontal', 'ba:left-medial-orbitofrontal'],
        electrodes: { eeg: false },
        time: 22,
      },
    ],
  },
  {
    id: 'neurons',
    name: 'Neurons',
    key: '5',
    tagline: 'A human cortical neuron firing on its own reconstructed arbor',
    // The template brain in MNI, where the cells lane places its cells: the run's placement fits as it is.
    // The engine draws BigBrain's HD cortex over it (decision 0006).
    pack: 'mni152-2009c-v0',
    // Spec §15's Cells and Model sections pick the run: one cell on its own arbor, or a human microcircuit
    // (the brief's "a microcircuit fires") in its healthy or depressed state.
    choices: [
      {
        id: 'run',
        label: 'Model',
        index: ['cells'],
        own: true,
        default: 'eyal2018-0603-11-bap-nmda-r2',
        options: [
          { value: 'eyal2018-0603-11-bap-nmda-r2', label: 'Eyal 0603_11', hint: 'Its own reconstruction, from human temporal cortex' },
          { value: 'h01-4873639924-bap-nmda-r2', label: 'H01 4873639924', hint: 'The same model on an arbor traced in H01’s electron micrographs', tagline: 'A human neuron model firing on an arbor traced in H01' },
          { value: 'yao2022-l23net-baseline', label: 'L2/3', hint: 'Yao 2022’s human L2/3 microcircuit: 1,000 cells', circuit: 'L2/3', condition: 'Healthy', tagline: 'A human L2/3 microcircuit of 1,000 cells firing' },
          { value: 'yao2022-l23net-depression', label: 'L2/3, depression', hint: 'The same circuit in depression, as Yao models it: SST cells’ inhibition 40 % weaker', circuit: 'L2/3', condition: 'Depression', tagline: 'The same L2/3 circuit in depression, its SST inhibition weakened' },
          { value: 'guetmccreight2022-l5net-test100-r2', label: 'L5', hint: 'Guet-McCreight 2022’s human L5 microcircuit: 100 cells', circuit: 'L5', condition: 'Healthy', tagline: 'A human L5 microcircuit of 100 cells firing' },
          // ACT-009 v3's two-layer column (docs/handoff/2026-10-04/column-10-08.md): 2,000 uncoupled cells, L2/3 above L5, soma
          // voltage and spikes from the reviewed seed-1234 run. Offered once ba-activity's playback run is in the cells index.
          {
            value: 'column-act009-v3-seed1234-playback-v1',
            label: 'Column (uncoupled)',
            hint: 'ACT-009 v3’s uncoupled column: 2,000 cells in two layers, L2/3 above L5, soma voltage and spikes (seed 1234)',
            circuit: 'Column (uncoupled)',
            condition: 'Uncoupled',
            tagline: 'An uncoupled column of 2,000 cells in two layers, L2/3 above L5, firing',
            // Placed in no brain until its coordinate audit: its close-up is the column alone, in the run's own frame (#2 5981063428).
            frame: true,
          },
        ],
      },
    ],
    // r2 supersedes the first run with the same data, adding probes and a pacing curve; the first is there for nodes without it.
    // A circuit too big to draw whole (Yao's 1,000 cells: 3.3 GB of voltage, and no placement) is drawn
    // through its close-up, some of its cells from the same simulation with every compartment recorded
    // and placed at the H01 site (r2's placement); its raster and rates still read every cell.
    sims: [
      { slot: 'main', candidates: ['{run}', 'cells/eyal2018-0603-11-bap-nmda-r2', 'cells/eyal2018-0603-11-bap-nmda'] },
      { slot: 'closeup', label: 'Drawn in 3D', candidates: ['{run}-closeup-r2'], drawnFor: 'main' },
    ],
    caption: 'Eyal 2018 human L2/3 pyramidal cell · NEURON',
    methods: 'One human L2/3 pyramidal neuron from temporal cortex (Eyal et al. 2018 model), simulated in NEURON on its own reconstructed morphology (cells lane).',
    // ba-activity's Methods line for H01's v2 playback, the run Its tissue plays (#2, 2026-10-02).
    notes: { 'h01-lif-resting-recurrent-seed731-v2': 'Suprathreshold ALN Poisson volleys through assumed LIF on a sparse segmentation-derived subgraph.' },
    status: 'preview',
    // Until the run loads; its header then gives the clock. The last speed is the default: 20 ms of the cell's time a second.
    timeline: { duration: 1.15, unit: 'ms', speeds: [0.005, 0.01, 0.02] },
    presets: [
      // The run's cells firing on their arbors, from behind and a little above: every run here is placed
      // with its pia towards the left (−x), so its cells lie side-on. An unplaced circuit draws in its own
      // frame where the viewport has frame scenes; its pia is +z there, so its layers stack up the screen.
      // Where none is drawn (a viewport before 0.3, or an unplaced circuit with no frame scene), the site
      // from the left, as Where it lives has it.
      {
        id: 'cells',
        name: 'Close up',
        hint: 'The run’s cells firing on their own arbors',
        camera: 'left',
        frameCells: { yawDeg: 180, pitchDeg: 20 },
        orbit: { targetMm: [-65.4, -4.7, -18.7], yawDeg: -90, pitchDeg: 0, distanceMm: 190 },
        // Everything else ghosted: the cells sit in the cortex, which would hide them.
        isolate: { ids: [] },
      },
      {
        id: 'origin',
        name: 'Where it lives',
        hint: 'Left anterior middle temporal gyrus, from the left',
        camera: 'left',
        // Centred on the H01 site (h01-patch-v0's placement: the pia over the left anterior MTG, MNI −65.4, −4.7, −18.7),
        // where the cells lane's column runs sit; this run's cell is 22 mm behind it on the gyrus's crown.
        orbit: { targetMm: [-65.4, -4.7, -18.7], yawDeg: -90, pitchDeg: 0, distanceMm: 190 },
        // The lobe's DKT gyri: its Julich-Brain areas overlap them, and their labels would too.
        isolate: {
          ids: [
            'ba:left-superior-temporal',
            'ba:left-banks-sts',
            'ba:left-middle-temporal',
            'ba:left-inferior-temporal',
            'ba:left-temporal-pole',
            'ba:left-transverse-temporal',
            'ba:left-fusiform',
            'ba:left-parahippocampal',
            'ba:left-entorhinal',
          ],
        },
        select: 'ba:left-middle-temporal',
      },
      { id: 'brain', name: 'In the brain', hint: 'The whole brain from the left, the gyrus outlined', camera: 'left', select: 'ba:left-middle-temporal' },
      {
        id: 'tissue',
        name: 'Its tissue',
        hint: 'H01, a cubic millimetre of this gyrus: every cell, layer and vessel',
        camera: 'anterior',
        pack: 'h01-patch-v0',
        // From the front of the slab (pack axes follow the cortex, +z to the pia): the column of
        // column.json, x −0.248 mm, pia to white matter (z 1.42 to −1.14), with its centre cell picked out.
        orbit: { targetMm: [-0.248, 0, 0.14], yawDeg: 0, pitchDeg: 0, distanceMm: 5.2 },
        select: 'neuron:26724',
      },
    ],
  },
  {
    id: 'drugs',
    name: 'Drugs',
    key: '6',
    tagline: 'Dose to blood to brain to receptor',
    // Pharma's runs are on fsLR: the HD cortex drawn over the template (BigBrain's) has a map from it, a subject pack doesn't.
    pack: TEMPLATE,
    choices: [
      {
        id: 'drug',
        label: 'Drug',
        index: ['pharma', 'pharma/run-20261001-v0'],
        kinds: ['engagement', 'pk-course', 'comparison'],
        // LSD's dose-to-cortex course stands in for its snapshot and its series-only PK run.
        supersede: { 'lsd-200ug-cortex-v1': ['lsd', 'lsd-200ug-pk'] },
        default: 'lsd-200ug-cortex-v1',
      },
    ],
    sims: [{ slot: 'main', candidates: ['{drug}'] }],
    // ba-activity's measured drug changes in functional connectivity (#684), which ba-web's ΔFC view reads by this
    // path (features/dfc.ts, DFC_RUN): claimed, so the Drug choice never lists it once pharma's index does.
    claims: ['pharma/measured-delta-fc-v1'],
    caption: 'Oral dose · plasma PK · receptor binding · PET receptor density',
    methods: 'Dose to blood to brain: plasma pharmacokinetics, equilibrium receptor binding and PET receptor density on the cortex (pharma lane).',
    status: 'preview',
    // Design's Drugs screens: 1:30 h after the dose, the engagement near its peak, on the brain alone.
    systems: ['cortex', 'cerebellum', 'brainstem'],
    start: 1.5 * 3600,
    timeline: { duration: 24 * 3600, unit: 'h', speeds: [600, 1800, 3600, 7200], events: [{ t: 0, label: 'Dose' }] },
    presets: [
      { id: 'whole', name: 'Whole brain', hint: 'The cortex from the front left', camera: 'hero', layers: BRAIN_ONLY },
      { id: 'left', name: 'Left hemisphere', hint: 'The lateral cortex', camera: 'left', layers: BRAIN_ONLY },
      { id: 'top', name: 'From above', hint: 'Both hemispheres', camera: 'superior', layers: BRAIN_ONLY },
      { id: 'back', name: 'Visual cortex', hint: 'The occipital lobes from behind', camera: 'posterior', layers: BRAIN_ONLY },
      // ba-web's measured ΔFC view (features/Dfc.tsx): ba-activity's twelve paired contrasts as a still change on the cortex.
      // A data view: only the cortex carries the scale, so the cerebellum and brainstem, which the export doesn't measure, stand
      // as ghosts rather than in the pack's own colours (the brainstem's orange read as a rise; lead, 02:16 UTC 10-03).
      { id: 'dfc', name: 'Measured ΔFC', hint: 'Twelve paired drug contrasts in resting connectivity', camera: 'left', layers: { ...BRAIN_ONLY, cerebellum: 'ghost', brainstem: 'ghost' }, still: true },
    ],
  },
  {
    // Mind › Vision (ba-web, with ba-mind's run): the visual world projected onto V1–V3. The template
    // brain's HD cortex carries map-fsaverage5, which places the retinotopy's fsaverage5 field on its folds.
    id: 'mind',
    name: 'Mind',
    key: '7',
    tagline: 'The visual world, projected onto V1–V3',
    pack: TEMPLATE,
    // ba-mind's rotating bar (sims/mind/benson14-vision-v3): a Gaussian line through fixation turning twice in
    // 12 s, sampled at each vertex's predicted receptive-field centre. v1 and v2 stay readable but aren't offered:
    // v1's activity unit the engine refused, v2 lacks the explicit period.
    sims: [{ slot: 'main', candidates: ['mind/benson14-vision-v3'], ramp: 'drive' }],
    caption: 'Benson 2014 retinotopy · stimulus projection · MNI152 2009c',
    methods: 'A bar turning in the visual field, projected onto V1–V3 through the Benson 2014 anatomy-based retinotopy template (neuropythy benson14 v4.0, fsaverage5) on the MNI152 2009c template brain: the stimulus luminance at each vertex’s predicted receptive-field centre, not neural activity. An image or camera you choose takes the bar’s place, sampled at the same centres by ba-mind’s live layer, in this browser only.',
    notes: { 'benson14-vision-v3': 'Stimulus luminance at each vertex’s predicted receptive-field centre, not neural activity. The left cortex represents the right visual field, and the cuneus, above the calcarine sulcus, the lower field.' },
    // The field is on the occipital cortex: the template's vessels, sinuses and nerves would only cover it.
    systems: ['cortex'],
    status: 'preview',
    // The bar is vertical at 0 s, horizontal at 3 s, and round again at 6 s; the run loops.
    timeline: { duration: 12, unit: 's', periodic: true, speeds: [0.25, 0.5, 1, 2], events: [{ t: 3, label: 'Horizontal' }, { t: 6, label: 'Vertical' }, { t: 9, label: 'Horizontal' }] },
    start: 1.5,
    presets: [
      // From behind, the bar diagonal so both hemispheres light; the medial wall of the left hemisphere with the
      // right cut away at the midline (V1 in the calcarine sulcus, V2 and V3 on the cuneus and lingual gyrus
      // around it); and from above, where the field's split between the hemispheres shows.
      { id: 'pole', name: 'Occipital pole', hint: 'V1 to V3 from behind', camera: 'posterior', callouts: ['ba:left-lateral-occipital', 'ba:right-cuneus'], time: 1.5 },
      // A section would put the slice's face between the camera and the wall; hiding the right hemisphere shows it.
      {
        id: 'medial',
        name: 'Medial wall',
        hint: 'The calcarine sulcus, with the right hemisphere hidden',
        camera: 'right',
        hide: ['ba:right-cerebral-hemisphere'],
        callouts: ['ba:left-pericalcarine', 'ba:left-cuneus', 'ba:left-lingual'],
        time: 1.5,
      },
      { id: 'above', name: 'From above', hint: 'The field split between the hemispheres', camera: 'superior', time: 4.5 },
      // Words into maps (ba-web, on ba-mind's MIND-003 model, sims/mind/neuroquery-native-v1, and its MIND-003C projection,
      // sims/mind/neuroquery-cortex-v1): a still view. The topic's map goes on the HD cortex through the viewport's live
      // layer in fsaverage5 space (features/textCortex.ts) and the native 4 mm volume is drawn in a strip of slices
      // (features/Text.tsx); the scene's run isn't sent.
      { id: 'text', name: 'Words into maps', hint: 'A topic from the literature, on the cortex and the model’s own 4 mm grid', camera: 'left', still: true },
    ],
  },
  {
    // Stimulate (ba-web, with ba-stim's FEM field): a TMS coil over left motor cortex on SimNIBS's Ernie example
    // head, and the electric field it induces on the cortex. The pack is Ernie's own head and cortex
    // (subject:simnibs:ernie); the run is sims/stim/ernie-tms-c3-v1, a static field the engine draws as a cortical
    // tint once it reads stim-sim-v0 (data/sims/layers.ts), or that features/stim.ts pushes through the vertex layer.
    id: 'stim',
    name: 'Stimulate',
    key: '8',
    tagline: 'A TMS coil over motor cortex, and the field it induces',
    pack: 'simnibs-ernie-head-v1',
    // The subject: Ernie, SimNIBS's example head, or IXI 165, one IXI participant's own cortex (the hero pack,
    // ixi-165-cortex-hd) carrying the field of a CHARM head built from their T1 and T2 (ba-stim's STIM-005). Each option
    // is data: its head (pack) and its runs (sets), one per stimulation it has, and the coil's drag basis under `grid`
    // (Ernie's nine poses within ±10 mm, STIM-001; IXI 165's four corners within ±5 mm, STIM-008; the panel reads each
    // bundle's own patch, landmark and result). The stimulation names which of the subject's runs plays (the main slot's
    // `{{stimulation}}` reads the value the subject set under that name; the grid slot's `{{stimulation}-grid}` reads
    // `tms-grid`, so a tDCS run, which drags nothing, has no basis slot), so tDCS is offered only where a subject has a tDCS
    // run: IXI 165's fixed C3–AF4 montage (ba-stim's STIM-007 export, kind tdcs).
    choices: [
      {
        id: 'subject',
        label: 'Subject',
        default: 'ernie',
        options: [
          { value: 'ernie', label: 'Ernie', hint: 'SimNIBS 4.6.0’s Ernie v4.1 native head, with the nine-pose live basis (±10 mm)', pack: 'simnibs-ernie-head-v1', sets: { tms: 'ernie-tms-c3-v1', 'tms-grid': 'ernie-tms-c3-grid-v1' } },
          // The run and the head are this option's data: STIM-005's hero-cortex run took the CHARM-head run's place here as a data change;
          // the -v2 ids (ba-stim's #1054, 10-04) are the same fields, cortex, coil and pads on a display scalp defaced with the shared IXI165 face mask.
          {
            value: 'ixi165',
            label: 'IXI 165',
            hint: 'IXI165-HH-1589’s own cortex, with the field of a CHARM head from their T1 and T2 sampled onto it (unvalidated automated tissue accuracy); the coil drags on a four-pose basis (±5 mm), and a tDCS montage',
            pack: 'ixi-165-cortex-hd',
            sets: { tms: 'ixi165-tms-c3-hero-v2', 'tms-grid': 'ixi165-tms-c3-patch10-hero-v2', tdcs: 'ixi165-tdcs-c3-af4-hero-v2' },
          },
        ],
      },
      {
        id: 'stimulation',
        label: 'Stimulation',
        default: 'tms',
        options: [
          { value: 'tms', label: 'TMS', hint: 'A Magstim 70 mm figure-eight coil over C3, pulsed: the field its current’s slew rate induces' },
          {
            value: 'tdcs',
            label: 'tDCS',
            hint: 'Two pads on the scalp, C3 +1 mA and AF4 −1 mA, direct current: the field between them, linear in the current',
            tagline: 'Two pads on the scalp, and the field between them',
          },
        ],
      },
    ],
    sims: [
      { slot: 'main', candidates: ['stim/{{stimulation}}'] },
      // The FEM grid around C3 the coil drags on (the subject's `tms-grid`: Ernie's nine poses, IXI 165's four): the app samples
      // it, the viewport never draws it as a run. A stimulation with no coil (tDCS) leaves the slot empty.
      { slot: 'grid', label: 'Live basis', appOnly: true, candidates: ['stim/{{stimulation}-grid}'] },
    ],
    notes: {
      'ixi165-tdcs-c3-af4-hero-v2':
        'A model, like the scene’s TMS fields: SimNIBS 4.6.0’s finite-element tDCS on the CHARM head built from IXI165’s T1 and T2, with its standard isotropic conductivities (white matter 0.126, grey 0.275, CSF 1.654, scalp 0.465, compact bone 0.008 S/m) and 4 mm gel/sponge pads at 1.0 S/m, C3 +1 mA (50 × 50 mm) against AF4 −1 mA (50 × 70 mm), the pads equipotential and the current normalised by the assembled boundary reactions; the field is sampled onto IXI165’s own FastSurfer cortex, 18,638 vertices (5.9 %) taking the nearest grey-matter node. The current control scales the fixed montage’s field linearly (0–2 mA; 0.286 V/m peak at 1 mA) and implies no threshold. Unvalidated: CHARM warned of an anisotropic image and possible extra meshing artefacts, automated tissue accuracy has no independent reference, and STIM-007 has not scored a measured field (research/2026-10-03-stim-ixi-tdcs.md). The ghost head under the pads is the run’s own display scalp, defaced with the shared IXI165 face mask (ba-stim’s -v2 export, #1054); the run’s Methods disclose the AF4 triangles kept to fit the pad.',
      'ixi165-tms-c3-hero-v2':
        'The field of a SimNIBS 4.6.0 CHARM head built from IXI165’s T1 and T2, sampled from the FEM at the vertices of IXI165’s own FastSurfer cortex (ba-stim’s STIM-005, research/2026-10-03-stim-ixi-hero-result.md). CHARM warned of an anisotropic image and possible extra meshing artefacts; 5.9 % of vertices took the nearest grey matter; the tissue accuracy is unvalidated, with no independent reference. The FEM’s scalp isn’t drawn with the TMS field; a head shown with it is the hero pack’s defaced surface, for context.',
    },
    caption: 'SimNIBS 4.6.0 · Ernie v4.1 native head · Magstim 70 mm figure-eight',
    methods: 'The electric field a Magstim 70 mm figure-eight coil over C3 induces on the cortex, from SimNIBS 4.6.0’s finite-element model of the subject’s head (Ernie v4.1’s native head, or a CHARM head built from IXI165’s T1 and T2), the coil a nominal 4 mm off the scalp with its handle toward CP3, at 1 MA/s: field magnitude in V/m, which scales with the coil current’s slew rate and is not activation or a motor threshold. Between the FEM poses of a grid around C3 (Ernie’s nine within ±10 mm, STIM-001, median error 3.9 %; IXI165’s four within ±5 mm, STIM-008, 2.0 %) the live field is bilinear in the signed vectors; the coil keeps its orientation, and nothing is shown outside the grid. IXI165’s tDCS is the same solver’s field for a fixed C3–AF4 montage, linear in the current. The cortex is coloured by |E| in the legend’s colormap; below 5 % of the legend’s range the field is not drawn; the shading is the surface’s own.',
    // Ernie's cortex and scalp are the pack: the scalp as a ghost so the coil's place over the head reads.
    systems: ['cortex', 'scalp'],
    status: 'preview',
    presets: [
      { id: 'lateral', name: 'From the left', hint: 'The coil over motor cortex', camera: 'left', layers: { scalp: 'ghost' } },
      { id: 'above', name: 'From above', hint: 'The figure-eight’s focus', camera: 'superior', layers: { scalp: 'ghost' } },
      { id: 'head', name: 'The head', hint: 'Ernie’s scalp over the brain', camera: 'hero', layers: { scalp: 'shown' } },
    ],
  },
  {
    // The brain over a lifetime: ba-life's charter scene, the ninth (the lead's go of 13:2x UTC 10-03). Its first track is
    // measured: the dHCP fetal white-matter surface atlas, 21 to 36 gestational weeks (Karolis et al. 2023), one growing
    // surface a hemisphere, played through the engine's morph layer on the pack of its week-21 geometry (lifetime-sim-v0;
    // engines/wgpu #743). The model track (LIFE-014's Newton fold) follows as a second slot once ba-life's export lands
    // (#2, 10-03). The clock is gestational age: the run's seconds since 21 weeks, read in weeks. The Period choice (LIFETIME_PERIOD)
    // swaps in ba-life's neonatal atlas (LIFETIME_NEONATAL, 37–44 weeks postmenstrual age) on its own pack and its own clock; the model
    // track stays with the fetal period.
    id: 'lifetime',
    name: 'Lifetime',
    key: '9',
    tagline: 'A cortex takes shape: 21 to 36 gestational weeks',
    pack: 'dhcp-fetal-wm-v1',
    // App-only: the readers open the run for the clock, the chart and Methods; the scene's own drive (features/lifetime.ts)
    // loads it into the viewport and lights it, as the 3D flow view drives its lumen, so the panel's map reaches the layer.
    choices: [LIFETIME_PERIOD],
    sims: [
      // The chosen period's sequence (LIFETIME_ATLAS or LIFETIME_NEONATAL, by the option's `sequence`).
      { slot: 'main', candidates: ['lifetime/{sequence}'], appOnly: true },
      // The model track: sections the strip overlays on the atlas's own (features/Lifetime.tsx); no viewport reads the format. Grown
      // from the fetal atlas's 21-week section, so it belongs to the fetal period alone.
      { slot: 'model', candidates: [LIFETIME_MODEL], label: 'Model', appOnly: true, when: { period: 'fetal' } },
      // Assigned thickness: LIFE-020's four fine-mesh tracks on an imposed-growth axis (lifetime-thickness-sections-v0), sections the strip
      // draws at a growth of their own (features/Lifetime.tsx); no viewport reads the format, and no clock maps to it. Grown from the same
      // 21-week white-matter section, so the fetal period's too.
      { slot: 'assigned', candidates: [LIFETIME_ASSIGNED], label: 'Assigned thickness', appOnly: true, when: { period: 'fetal' } },
    ],
    caption: 'Measured atlas · dHCP fetal white matter, 21–36 gestational weeks',
    methods: 'The dHCP fetal white-matter surface atlas (Karolis et al. 2023, 242 in-utero scans): sixteen weekly population-average surfaces from 21 to 36 gestational weeks, played as one growing surface a hemisphere; the frames between weeks are linear interpolation, not growth. Beside it, the model track: LIFE-014’s plane-strain Newton fold as a 2D section over the atlas’s own section at the same clock (LIFE-015 killed its atlas agreement; the clock is assumed).',
    systems: ['white-matter'],
    status: 'preview',
    start: 0,
    timeline: { duration: 15 * WEEK_S, unit: 'week', origin: 21, speeds: LIFETIME_SPEEDS },
    // The pack is the white matter alone, hidden by default everywhere else (data/catalogue.ts DEFAULT_MODES): every view shows it.
    presets: [
      { id: 'side', name: 'From the left', hint: 'The hemispheres growing, from the side', camera: 'left', layers: LIFETIME_LAYERS },
      { id: 'above', name: 'From above', hint: 'Both hemispheres, the folds deepening', camera: 'superior', layers: LIFETIME_LAYERS },
      { id: 'front', name: 'From the front', hint: 'The hemispheres face on', camera: 'anterior', layers: LIFETIME_LAYERS },
      // Later in life: cortical thinning by Parkinson's stage on the MNI template's cortex, a still view on its own pack (the
      // fetal sequence stands aside, no clock: a stage is an ordinal step, never time), the stage map through the viewport's
      // overlay (features/lifetime.ts, LIFETIME_PD).
      {
        id: PD_VIEW,
        name: 'Parkinson’s stages',
        hint: 'Cortical thinning by Hoehn and Yahr stage: measured (ENIGMA-PD) beside a connectome model',
        tagline: 'Parkinson’s stages: ENIGMA-PD’s measured cortical thinning beside a connectome model',
        camera: 'left',
        pack: TEMPLATE,
        packKind: 'anatomy',
        still: true,
        layers: { ...BRAIN_ONLY, cerebellum: 'ghost', brainstem: 'ghost' },
      },
      // Across a lifetime: Rutherford 2022's expected cortical thickness on fsaverage's Destrieux cortex, a still view on its own pack
      // with its own age control, years since birth (no clock; nothing maps it to the fetal weeks or BrainChart's post-conception
      // age), the parcel medians expanded over the vertices through the viewport's live vertex layer (features/lifetime.ts,
      // LIFETIME_THICKNESS).
      {
        id: THICKNESS_VIEW,
        name: 'Cortical thickness',
        hint: 'Expected cortical thickness, 2 to 100 years since birth: Rutherford 2022’s normative medians on fsaverage’s Destrieux parcels',
        tagline: 'Expected cortical thickness: 2 to 100 years since birth, a normative-model reference',
        camera: 'left',
        pack: 'fsaverage-destrieux-v1',
        packKind: 'anatomy',
        still: true,
        layers: { cortex: 'shown' },
      },
    ],
  },
];

export const scenarioById = (id: string): Scenario => SCENARIOS.find((s) => s.id === id) ?? SCENARIOS[0]!;

/** The structures the scene's current view names first (ScenePreset.callouts). */
export const viewCallouts = (s: { scenario: string; preset: string | null }): readonly string[] => scenarioById(s.scenario).presets.find((p) => p.id === s.preset)?.callouts ?? [];
